import * as React from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Dense, spreadsheet-style data grid.
 *
 * columns: [{
 *   key, header, align ("left"|"right"|"center"), width,
 *   render?(row): node, sortable?: bool, sortValue?(row): any,
 *   headerClassName, cellClassName, footer?: node
 * }]
 * rows: array
 * getRowId(row): string
 * onRowClick?(row)
 * rowActions?(row): node  — shown in a trailing cell, revealed on row hover
 * quickAdd?: {
 *   fields: [{ key, placeholder, type, options, autoFocus }],
 *   onSubmit(draft, reset),
 *   submitting?: bool,
 *   addLabel?: string
 * }
 * initialSort?: { key, dir: "asc"|"desc" }
 */
export function DataGrid({
  columns,
  rows,
  getRowId = (row) => row._id ?? row.id,
  onRowClick,
  rowActions,
  quickAdd,
  initialSort,
  emptyState,
  className,
  maxHeight,
}) {
  const [sort, setSort] = React.useState(initialSort ?? { key: null, dir: "asc" });
  const hasFooter = columns.some((column) => column.footer != null);
  const hasTrailing = Boolean(rowActions) || Boolean(quickAdd);

  const sorted = React.useMemo(() => {
    if (!sort.key) return rows;
    const column = columns.find((item) => item.key === sort.key);
    if (!column) return rows;
    const valueOf = column.sortValue || ((row) => row[column.key]);
    return [...rows].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return sort.dir === "asc" ? av - bv : bv - av;
      }
      return sort.dir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [rows, sort, columns]);

  function toggleSort(key) {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  }

  const alignClass = (align) =>
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";

  const totalCols = columns.length + (hasTrailing ? 1 : 0);

  return (
    <div
      className={cn(
        "relative w-full overflow-auto rounded-xl border border-border bg-card shadow-sm",
        className
      )}
      style={maxHeight ? { maxHeight } : undefined}
    >
      <table className="w-full border-collapse text-[13px]">
        <thead className="sticky top-0 z-10 bg-muted/70 backdrop-blur">
          <tr className="border-b border-border">
            {columns.map((column) => {
              const active = sort.key === column.key;
              return (
                <th
                  key={column.key}
                  style={column.width ? { width: column.width } : undefined}
                  className={cn(
                    "select-none whitespace-nowrap px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground",
                    alignClass(column.align),
                    column.sortable && "cursor-pointer hover:text-foreground",
                    column.headerClassName
                  )}
                  onClick={column.sortable ? () => toggleSort(column.key) : undefined}
                >
                  <span
                    className={cn(
                      "inline-flex items-center gap-1",
                      column.align === "right" && "flex-row-reverse"
                    )}
                  >
                    {column.header}
                    {column.sortable ? (
                      active ? (
                        sort.dir === "asc" ? (
                          <ArrowUp className="h-3 w-3 text-foreground" />
                        ) : (
                          <ArrowDown className="h-3 w-3 text-foreground" />
                        )
                      ) : (
                        <ChevronsUpDown className="h-3 w-3 opacity-40" />
                      )
                    ) : null}
                  </span>
                </th>
              );
            })}
            {hasTrailing ? <th className="w-px px-2" /> : null}
          </tr>
        </thead>

        <tbody>
          {sorted.length === 0 && !quickAdd ? (
            <tr>
              <td colSpan={totalCols} className="px-3 py-10 text-center text-muted-foreground">
                {emptyState || "No records yet."}
              </td>
            </tr>
          ) : (
            sorted.map((row) => (
              <tr
                key={getRowId(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  "group border-b border-border/60 transition-colors hover:bg-muted/40",
                  onRowClick && "cursor-pointer"
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      "px-3 py-2 align-middle",
                      alignClass(column.align),
                      column.align === "right" && "tabular-nums",
                      column.cellClassName
                    )}
                  >
                    {column.render ? column.render(row) : row[column.key] ?? "—"}
                  </td>
                ))}
                {hasTrailing ? (
                  <td className="px-2 align-middle">
                    {rowActions ? (
                      <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                        {rowActions(row)}
                      </div>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            ))
          )}

          {quickAdd ? <QuickAddRow columns={columns} quickAdd={quickAdd} /> : null}
        </tbody>

        {hasFooter ? (
          <tfoot className="sticky bottom-0 bg-muted/70 backdrop-blur">
            <tr className="border-t border-border font-semibold">
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cn(
                    "px-3 py-2.5",
                    alignClass(column.align),
                    column.align === "right" && "tabular-nums"
                  )}
                >
                  {column.footer ?? ""}
                </td>
              ))}
              {hasTrailing ? <td /> : null}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

function QuickAddRow({ columns, quickAdd }) {
  const fieldByKey = React.useMemo(() => {
    const map = new Map();
    for (const field of quickAdd.fields) map.set(field.key, field);
    return map;
  }, [quickAdd.fields]);

  const [draft, setDraft] = React.useState({});

  function reset() {
    setDraft({});
  }
  function submit() {
    quickAdd.onSubmit(draft, reset);
  }
  function onKeyDown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      submit();
    }
  }

  return (
    <tr className="border-t border-border bg-emerald-500/[0.06]">
      {columns.map((column) => {
        const field = fieldByKey.get(column.key);
        if (!field) return <td key={column.key} className="px-3 py-1.5" />;
        return (
          <td key={column.key} className="px-2 py-1.5">
            {field.type === "select" ? (
              <select
                value={draft[field.key] ?? ""}
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, [field.key]: event.target.value }))
                }
                onKeyDown={onKeyDown}
                className="h-8 w-full rounded-md border border-border bg-background px-2 text-[13px] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">{field.placeholder || "Select"}</option>
                {(field.options || []).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={field.type || "text"}
                value={draft[field.key] ?? ""}
                placeholder={field.placeholder}
                autoFocus={field.autoFocus}
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, [field.key]: event.target.value }))
                }
                onKeyDown={onKeyDown}
                className={cn(
                  "h-8 w-full rounded-md border border-border bg-background px-2 text-[13px] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary",
                  column.align === "right" && "text-right tabular-nums"
                )}
              />
            )}
          </td>
        );
      })}
      <td className="px-2 py-1.5">
        <button
          type="button"
          onClick={submit}
          disabled={quickAdd.submitting}
          className="inline-flex h-8 items-center gap-1 rounded-md bg-primary px-2.5 text-[13px] font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
          aria-label={quickAdd.addLabel || "Add row"}
        >
          <Plus className="h-3.5 w-3.5" />
          {quickAdd.submitting ? "…" : "Add"}
        </button>
      </td>
    </tr>
  );
}

export default DataGrid;
